import { Requisition, RequisitionItem, PaymentOrder, PaymentOrderItem, DeliveryNote, DeliveryNoteItem } from '../../types';

export interface ItemRow {
  requisition: Requisition;
  item: RequisitionItem;
  /** True for a synthetic row representing a requisition that has no items yet */
  placeholder?: boolean;
}

export const statusConfig: Record<string, { label: string; color: string }> = {
  pending: { label: 'Pending', color: 'bg-yellow-100 text-yellow-800' },
  rejected: { label: 'Rejected', color: 'bg-red-100 text-red-800' },
  approved: { label: 'Approved', color: 'bg-green-100 text-green-800' },
  purchased: { label: 'Purchased', color: 'bg-blue-100 text-blue-800' },
  'delivery-noted': { label: 'Delivery Noted', color: 'bg-purple-100 text-purple-800' },
  delivered: { label: 'Delivered', color: 'bg-indigo-100 text-indigo-800' },
};

export const EMPTY_ITEM = { item: '', qty: 0, rob: '', appQty: 0, unitPrice: 0, supplier: '', remarks: '', preparedBy: '' };

/** Unique key for a row. Item ids alone can collide across requisitions
 *  (older forms saved the first item with id "1" on every requisition). */
export const rowKey = (row: ItemRow): string =>
  `${row.requisition.documentId || row.requisition.id}:${row.item.id}`;

const today = () => new Date().toISOString().split('T')[0];
const shortId = () => Date.now().toString().slice(-6);

/** Items of a requisition, tolerating null / non-array shapes from the API */
export function itemsOf(req: Requisition): RequisitionItem[] {
  return Array.isArray(req?.items) ? req.items : [];
}

/** Flatten all requisitions into one row per item. Requisitions without any
 *  items still get a placeholder row so they stay visible on the screen. */
export function flattenItems(requisitions: Requisition[]): ItemRow[] {
  const rows: ItemRow[] = [];
  (requisitions || []).forEach((req) => {
    const items = itemsOf(req);
    if (items.length === 0) {
      rows.push({
        requisition: req,
        placeholder: true,
        item: {
          id: `placeholder-${req.documentId || req.id}`,
          itemNumber: 0, item: '', qty: 0, rob: '', appQty: 0, unitPrice: 0,
          supplier: '', remarks: '', selected: false,
          status: (req.status as RequisitionItem['status']) || 'pending',
        } as RequisitionItem,
      });
      return;
    }
    items.forEach((item) => rows.push({ requisition: req, item }));
  });
  return rows;
}

const reqIdOf = (req: Requisition) => req.documentId || String(req.id);

/** Full-data payment order line built from one requisition item */
function poItem(req: Requisition, it: RequisitionItem, no: number, approvedIds: Set<string>): PaymentOrderItem {
  const qty = Number(it.qty) || 0;
  const appQty = Number(it.appQty) || 0;
  const unitPrice = Number(it.unitPrice) || 0;
  return {
    id: it.id, no, details: it.item, unit: '',
    qty, rob: it.rob || '', appQty, unitPrice,
    total: (appQty || qty) * unitPrice,
    supplier: it.supplier || '',
    status: approvedIds.has(it.id) ? 'approved'
      : (['approved', 'purchased'].includes(it.status) ? it.status as any : 'pending'),
    requisitionId: reqIdOf(req), requisitionItemId: it.id,
  };
}

const sumTotals = (items: PaymentOrderItem[]) => items.reduce((s, i) => s + (Number(i.total) || 0), 0);

/** One payment order per requisition, carrying ALL its items with full data.
 *  employeeName is the employee chosen in the approval popup. */
export function buildPaymentOrderPayload(req: Requisition, providers: any[], employeeName?: string, approvedIds: Set<string> = new Set()): any {
  const items = itemsOf(req).map((it, i) => poItem(req, it, i + 1, approvedIds));
  const subtotal = sumTotals(items);
  const suppliers = itemsOf(req).map((i) => i.supplier).filter(Boolean);
  const provider = (providers || []).find(
    (p: any) => suppliers.some((s) => p.name?.toLowerCase() === String(s).toLowerCase())
  );
  return {
    orderNumber: `PO-${shortId()}`,
    date: today(),
    referenceNo: req.formNumber || '',
    ...(provider ? { providerId: provider.documentId || String(provider.id) } : {}),
    paymentMethod: 'cash',
    items,
    subtotal, taxes: 0, discount: 0, total: subtotal,
    status: 'pending',
    requestedBy: employeeName || req.preparedBy || 'System',
    authorizedByName: req.approvedByManager || req.preparedBy || 'System',
    authorizedByJobTitle: 'Manager',
    comments: `Auto-created from requisition ${req.formNumber}${employeeName ? ` — assigned to ${employeeName}` : ''}`,
  };
}

/** Refresh an existing order with the requisition's current items, keeping
 *  per-item progress (purchased stays purchased) and marking approvedIds approved */
export function mergePaymentOrderItems(order: PaymentOrder, req: Requisition, approvedIds: Set<string>): { items: PaymentOrderItem[]; subtotal: number; total: number } {
  const prev = new Map((order.items || []).map((i: any) => [String(i.requisitionItemId || i.id), i]));
  const items = itemsOf(req).map((it, i) => {
    const fresh = poItem(req, it, i + 1, approvedIds);
    const old: any = prev.get(String(it.id));
    if (old?.status === 'purchased') fresh.status = 'purchased';
    else if (old?.status === 'approved' && fresh.status === 'pending') fresh.status = 'approved';
    return fresh;
  });
  const subtotal = sumTotals(items);
  return { items, subtotal, total: subtotal + (Number(order.taxes) || 0) - (Number(order.discount) || 0) };
}

/** Find the auto-created payment order already linked to a requisition */
export function findOrderForReq(orders: PaymentOrder[], req: Requisition): PaymentOrder | undefined {
  const rid = reqIdOf(req);
  return (orders || []).find((o) =>
    (o.items || []).some((i: any) => String(i.requisitionId || '') === rid || String(i.requisitionId || '') === String(req.id)) ||
    (!!req.formNumber && o.referenceNo === req.formNumber));
}

/** Full-data delivery note line built from one requisition item */
function dnItem(it: RequisitionItem, no: number): DeliveryNoteItem {
  return {
    id: it.id, no, type: it.item,
    quantity: Number(it.appQty) || Number(it.qty) || 0, unitSize: '',
    qty: Number(it.qty) || 0, rob: it.rob || '', appQty: Number(it.appQty) || 0,
    unitPrice: Number(it.unitPrice) || 0, supplier: it.supplier || '',
    status: 'pending', requisitionItemId: it.id,
  };
}

/** Payload for the delivery note auto-created for a set of requisition items */
export function buildDeliveryNotePayload(req: Requisition, items: RequisitionItem[]): any {
  const number = `DN-${shortId()}`;
  return {
    requisitionId: reqIdOf(req),
    deliveryNoteNumber: number,
    noteNo: number,
    workSite: req.location || '',
    vessel: req.vessel || '',
    date: today(),
    projectLocation: req.location || '',
    technicalPart: items.map((it, i) => dnItem(it, i + 1)),
    status: 'pending',
    deliveryDate: today(),
    deliveredByName: req.preparedBy || 'System',
    deliveredToName: req.vessel || 'Vessel',
    notes: `Auto-created from requisition ${req.formNumber}`,
  };
}

/** Merge more requisition items into an existing note's technical part (no duplicates) */
export function mergeDeliveryNoteItems(note: DeliveryNote, req: Requisition, items: RequisitionItem[]): DeliveryNoteItem[] {
  const existing = Array.isArray(note.technicalPart) ? [...note.technicalPart] : [];
  const seen = new Set(existing.map((i: any) => String(i.requisitionItemId || i.id)));
  items.forEach((it) => {
    if (!seen.has(String(it.id))) existing.push(dnItem(it, existing.length + 1));
  });
  return existing.map((i, idx) => ({ ...i, no: idx + 1 }));
}

/** Find the delivery note already linked to a requisition */
export function findNoteForReq(notes: DeliveryNote[], req: Requisition): DeliveryNote | undefined {
  const rid = reqIdOf(req);
  return (notes || []).find((n) => String(n.requisitionId || '') === rid || String(n.requisitionId || '') === String(req.id));
}

/** Replace items on a requisition, returning the data payload for the update call */
export function withUpdatedItems(req: Requisition, items: RequisitionItem[]): any {
  return { ...req, documentId: req.documentId, items };
}
